import { type FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import { OrgChartSidePanelDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'

import { useOrgChartState } from 'modules/org_chart/components/Provider'
import ProfileDetails from './ProfileDetails'
import ReportsDetails from './ReportsDetails'

interface Props {
  allEmployees: GraphQLEmployee[]
  employeeMap: Map<string, GraphQLEmployee>
  onClose: () => void
  canSeeJobTitle: boolean
  canSeeJobCatalog: boolean
  canSeeJobCatalogTree: boolean
  hasLegacyJobCatalog: boolean
}

const SidePanel: FC<Props> = ({
  onClose,
  allEmployees,
  employeeMap,
  canSeeJobTitle,
  canSeeJobCatalog,
  canSeeJobCatalogTree,
  hasLegacyJobCatalog,
}) => {
  const { update, activeEmployee, activeSidePanelTab } = useOrgChartState()
  const employeeId = activeEmployee?.id
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data, loading } = useQuery(OrgChartSidePanelDocument, {
    variables: {
      employeeId: String(employeeId),
    },
    skip: !employeeId,
  })
  const employee = data?.employees?.employee
  const employeeFullName = data?.employees?.employee?.fullName
  const managedEmployees = data?.employees?.employeesConnection?.nodes ?? []
  const hasManagedEmployees = managedEmployees.length > 0
  const canSeeTeams = data?.teams?._policies?.companySeeTeams?.can ?? false

  const handleFooterActionClick = () => {
    if (!activeEmployee?.id) return

    navigate(navigation.employees.id.fullPath({ id: activeEmployee.id }))
    track(trackingEvents.orgChart.sidepanel.footerActionClick)
  }

  const handleClose = () => {
    track(trackingEvents.orgChart.sidepanel.close)
    onClose()
  }

  const trackTabChange = (tabId: string) => {
    switch (tabId) {
      case 'profile':
        track(trackingEvents.orgChart.sidepanel.tabs.profileClick)
        break
      case 'reports':
        track(trackingEvents.orgChart.sidepanel.tabs.reportsClick)
        break
    }
  }

  const handleTabClick = (id: string) => {
    update('activeSidePanelTab', id)
    trackTabChange(id)
  }

  if (!employee || loading) {
    return null
  }

  return (
    <Sidepanel
      width="narrow"
      title={employeeFullName}
      onClose={handleClose}
      closeLabel={i18n.t('common.close')}
      overlay={false}
      open={!!employeeId}
      boxPadding={false}
      allowBackgroundInteraction
    >
      <>
        <Box height="full" overflowY="scroll">
          {hasManagedEmployees && (
            <Tabs tabsAlign="center" currentId={activeSidePanelTab} onTabClicked={handleTabClick}>
              <Tabs.Tab
                id="profile"
                label={i18n.t('employees.orgchart.profile')}
                default={activeSidePanelTab === 'profile'}
              >
                {activeEmployee && (
                  <ProfileDetails
                    employee={employee}
                    canSeeTeams={canSeeTeams}
                    canSeeJobTitle={canSeeJobTitle}
                    canSeeJobCatalog={canSeeJobCatalog}
                    canSeeJobCatalogTree={canSeeJobCatalogTree}
                    hasLegacyJobCatalog={hasLegacyJobCatalog}
                  />
                )}
              </Tabs.Tab>
              <Tabs.Tab
                id="reports"
                label={i18n.t('employees.orgchart.reports')}
                default={activeSidePanelTab === 'reports'}
              >
                <ReportsDetails allEmployees={allEmployees} employeeMap={employeeMap} />
              </Tabs.Tab>
            </Tabs>
          )}
          {!hasManagedEmployees && !!activeEmployee && (
            <ProfileDetails
              employee={employee}
              canSeeTeams={canSeeTeams}
              canSeeJobTitle={canSeeJobTitle}
              canSeeJobCatalog={canSeeJobCatalog}
              canSeeJobCatalogTree={canSeeJobCatalogTree}
              hasLegacyJobCatalog={hasLegacyJobCatalog}
            />
          )}
        </Box>
        <Box
          background="grey100"
          padding="s16"
          alignItems="flexEnd"
          border={{
            top: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
        >
          <Button type="primary" onClick={handleFooterActionClick}>
            {i18n.t('employees.orgchart.go_to_profile')}
          </Button>
        </Box>
      </>
    </Sidepanel>
  )
}

export default observer(SidePanel)
